import { useMemo } from "react";
import { Checkbox, Group, SegmentedControl, Stack, Text } from "@mantine/core";
import {
  HOME_SCREEN_CARD_STYLES,
  HOME_SCREEN_SECTION_DEFAULTS,
} from "@congrs/schemas";

import { useGetSponsorLevels } from "../../../../api/sponsors";

const CARD_STYLE_LABELS = {
  compact: "Compact",
  grid: "Grid",
  wide: "Wide",
};
const CARD_STYLE_DATA = HOME_SCREEN_CARD_STYLES.map((value) => ({
  value,
  label: CARD_STYLE_LABELS[value],
}));

const useSponsorLevels = (eventId) => {
  const { data } = useGetSponsorLevels({ eventId });

  return useMemo(
    () => [...(data ?? [])].sort((a, b) => a.order - b.order),
    [data]
  );
};

// Unconfigured means the top level alone, the same fallback the app applies.
const selectedIds = (options, levels) =>
  options.levels ?? levels.slice(0, 1).map((level) => level.id);

/** The row's one-line summary: which levels it shows, and how. */
const Summary = ({ eventId, options }) => {
  const levels = useSponsorLevels(eventId);

  if (levels.length === 0) return null;

  // A level picked earlier can since have been deleted, and no sponsor sits in
  // it any more, so it drops out of the summary the way it does out of the app.
  const names = selectedIds(options, levels)
    .map((id) => levels.find((level) => level.id === id)?.name)
    .filter(Boolean);

  if (names.length === 0) return "No levels selected · hidden";

  const style = CARD_STYLE_LABELS[options.cardStyle].toLowerCase();

  return `${names.join(", ")} · ${style} cards`;
};

const Settings = ({ eventId, options, onChange }) => {
  const levels = useSponsorLevels(eventId);

  return (
    <Stack gap="md" mt="md" pl={34}>
      <Group gap="xl" wrap="nowrap" align="flex-start">
        <Text size="sm" fw={500} w={110} style={{ flexShrink: 0 }}>
          Sponsor levels
        </Text>
        {levels.length === 0 ? (
          <Text size="sm" c="dimmed">
            This event has no sponsor levels yet.
          </Text>
        ) : (
          <Checkbox.Group
            value={selectedIds(options, levels).map(String)}
            onChange={(value) =>
              onChange({
                ...options,
                // Kept in rank order so the summary reads top level first.
                levels: levels
                  .map((level) => level.id)
                  .filter((id) => value.includes(String(id))),
              })
            }
          >
            <Group gap="md">
              {levels.map((level) => (
                <Checkbox
                  key={level.id}
                  value={String(level.id)}
                  label={level.name}
                />
              ))}
            </Group>
          </Checkbox.Group>
        )}
      </Group>

      <Group gap="xl" wrap="nowrap">
        <Text size="sm" fw={500} w={110} style={{ flexShrink: 0 }}>
          Card style
        </Text>
        <SegmentedControl
          size="xs"
          data={CARD_STYLE_DATA}
          value={options.cardStyle}
          onChange={(cardStyle) => onChange({ ...options, cardStyle })}
        />
      </Group>

      {selectedIds(options, levels).length > 0 && (
        <Text size="xs" c="dimmed">
          The section is hidden when no sponsor sits in the levels you picked.
        </Text>
      )}
    </Stack>
  );
};

const sponsorSectionSettings = {
  defaults: HOME_SCREEN_SECTION_DEFAULTS.sponsors,
  Settings,
  Summary,
};

export default sponsorSectionSettings;
